import i18n from '@/utils/i18n';
import React, { useCallback, useEffect, useState } from 'react';
import { ActionSheetContent } from './action-sheet';
import { StyleSheet, View } from 'react-native';
import { useBluetoothContext } from '@/contexts/bluetooth-context';
import { useToasts } from '@/contexts/toast-context';

interface Props {
  goBack: () => void;
  onPermissionsGranted?: () => void;
}

export const TagPrintingBluetoothPermissions: React.FC<Props> = ({
  goBack,
  onPermissionsGranted,
}) => {
  const { canRequestPermission, hasPermission, requestPermission } =
    useBluetoothContext();
  const { showErrorToast } = useToasts();
  const [permissionsLoading, setPermissionsLoading] = useState(false);

  useEffect(() => {
    if (!canRequestPermission && !hasPermission) {
      showErrorToast(i18n.t('bluetooth_permissions.denied'));
    }
    // only run on mount, prevent state updates in bluetooth context
    // incorrectly triggering this effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAction = useCallback(
    // prefacing action with _unused to mark it as unused
    async (_action: string, index: number) => {
      setPermissionsLoading(true);
      try {
        if (index === 0) {
          const granted = await requestPermission();
          if (granted) {
            onPermissionsGranted?.();
            return;
          }
          showErrorToast(i18n.t('bluetooth_permissions.denied'));
        }
        goBack();
      } catch (error) {
        showErrorToast(i18n.t('bluetooth_permissions.error'));
      } finally {
        setPermissionsLoading(false);
      }
    },
    [goBack, onPermissionsGranted, requestPermission, showErrorToast],
  );

  return (
    <View style={styles.base}>
      <ActionSheetContent
        actions={[i18n.t('turn_on'), i18n.t('cancel')]}
        loadingActionIndex={permissionsLoading ? 0 : undefined}
        message={i18n.t('bluetooth_permissions.message')}
        onAction={handleAction}
        primaryIndices={[0]}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  base: {
    flexDirection: 'column',
  },
});
